/**
 * nativeDialog.ts
 *
 * Thin, typed wrapper around the native @capacitor/dialog plugin, obtained via
 * Capacitor's `registerPlugin` so this module type-checks against @capacitor/core
 * alone (the plugin's native implementation is wired up by `npx cap sync`).
 *
 * SECURITY: transaction confirmation MUST use a native dialog, not an overlay
 * injected into the dApp WebView. The InAppBrowser page is fully controlled by
 * the (potentially malicious) dApp — any button we render inside it can be
 * synthetically clicked or its handler intercepted from the page's own JS, so an
 * in-WebView "Approve" is forgeable. A native AlertDialog is drawn by the OS
 * outside the WebView and cannot be reached by page scripts.
 */

import { registerPlugin } from '@capacitor/core'

export interface NativeConfirmOptions {
  title?: string
  message: string
  okButtonTitle?: string
  cancelButtonTitle?: string
}

interface DialogPlugin {
  confirm(options: NativeConfirmOptions): Promise<{ value: boolean }>
  alert(options: { title?: string; message: string; buttonTitle?: string }): Promise<void>
}

/** Resolves to the natively-registered "Dialog" plugin (@capacitor/dialog). */
export const NativeDialog = registerPlugin<DialogPlugin>('Dialog')

/**
 * Show a native confirmation dialog. Returns true only if the user tapped the
 * native OK button. Throws are surfaced to the caller so a failed/unavailable
 * dialog is treated as a rejection (fail-closed) by the transaction gate.
 */
export async function nativeConfirm(options: NativeConfirmOptions): Promise<boolean> {
  const { value } = await NativeDialog.confirm(options)
  return value === true
}
